import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FaTruckMoving, FaSearch, FaSpinner, FaCheck,
  FaExclamationTriangle, FaClipboardCheck,
  FaLink, FaHistory, FaEdit
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import { API_URL } from '../config';

const TERMS = [
  'Pay in Advance (Prepaid)', 'COD', '7 Days', '15 Days', '30 Days',
  '50% Booking/50% EPOD', '50% Booking/50% Completion'
];

function SuppliersPage({ onLogout }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [termsModal, setTermsModal] = useState(null);
  const [savingTerms, setSavingTerms] = useState(false);
  const [overrideModal, setOverrideModal] = useState(null);
  const [savingOverride, setSavingOverride] = useState(false);
  const [pushTarget, setPushTarget] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/subcontractors`);
      const data = await response.json();
      if (data.success) setSuppliers(data.subcontractors || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const filtered = suppliers.filter((s) =>
    (s.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.contact_person || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openTermsModal = (s) => setTermsModal({ supplier: s, terms: s.payment_terms || '30 Days', credit_limit: s.credit_limit || 0 });

  const saveTerms = async () => {
    setSavingTerms(true);
    try {
      const res = await fetch(`${API_URL}/payment-terms/subcontractor/${termsModal.supplier.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_terms: termsModal.terms, credit_limit: parseFloat(termsModal.credit_limit) || 0 })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Payment terms updated');
        setTermsModal(null);
        fetchSuppliers();
      } else {
        toast.error(data.error || 'Failed to update');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update terms');
    } finally {
      setSavingTerms(false);
    }
  };

  const saveOverride = async () => {
    setSavingOverride(true);
    try {
      const res = await fetch(`${API_URL}/bank-details/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subcontractor_id: overrideModal.supplier.id, reason: overrideModal.reason })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Bank details verified for QuickBooks');
        setOverrideModal(null);
      } else {
        toast.error(data.error || 'Override failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('Override failed');
    } finally {
      setSavingOverride(false);
    }
  };

  const pushToQbo = async () => {
    setPushing(true);
    try {
      const res = await fetch(`${API_URL}/bank-details/push-to-qbo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subcontractor_id: pushTarget.id })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Bank details pushed to QuickBooks');
      } else {
        toast.error(data.error || 'Push failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('Push failed');
    } finally {
      setPushing(false);
      setPushTarget(null);
    }
  };

  const openHistory = async (s) => {
    setHistoryFor(s);
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_URL}/bank-details/history?subcontractor_id=${s.id}`);
      const data = await res.json();
      setHistory(data.history || []);
    } catch (error) {
      console.error(error);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const badge = (status) => {
    const map = {
      PENDING: { bg: '#FEF3C7', color: '#92400E', label: 'Pending confirmation' },
      CONFIRMED: { bg: '#DBEAFE', color: '#1E40AF', label: 'Confirmed by subbie' },
      VERIFIED_FOR_QBO: { bg: '#D1FAE5', color: '#065F46', label: 'Verified for QuickBooks' },
      NEEDS_REVIEW: { bg: '#FEE2E2', color: '#991B1B', label: 'Change — needs review' }
    };
    const m = map[status] || { bg: '#E5E7EB', color: '#374151', label: status };
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: m.bg, color: m.color }}>{m.label}</span>;
  };


  return (
    <AdminLayout activeTab="suppliers" onLogout={onLogout}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-navy flex items-center gap-3">
          <FaTruckMoving className="text-green-600" /> Suppliers
        </h1>
        <p className="text-sm text-gray-500 mt-1">Subcontractors, payment terms and bank-details verification status.</p>
      </div>

      <div className="relative max-w-md mb-6">
        <FaSearch className="absolute left-3 top-3 text-gray-400" size={14} />
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search suppliers..."
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-gray-500">
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Reg / VAT</th>
                <th className="px-4 py-3 font-medium">Payment Terms</th>
                <th className="px-4 py-3 font-medium">Credit Limit</th>
                <th className="px-4 py-3 font-medium">Bank Details</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{s.company_name}</div>
                    <div className="text-xs text-gray-400">{s.trading_name || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{s.contact_person}<div className="text-xs text-gray-400">{s.contact_phone}</div></td>
                  <td className="px-4 py-3 text-xs text-gray-500">Reg: {s.registration_number || '—'}<div>VAT: {s.vat_number || '—'}</div></td>
                  <td className="px-4 py-3 text-gray-700">{s.payment_terms || '30 Days'}</td>
                  <td className="px-4 py-3 text-gray-700">R {parseFloat(s.credit_limit || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">{badge(s.bank_details_status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openTermsModal(s)}
                        title="Edit payment terms"
                        className="p-2 rounded-lg hover:bg-green-50 text-gray-500 hover:text-green-600"
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={() => openHistory(s)}
                        title="Bank details history"
                        className="p-2 rounded-lg hover:bg-green-50 text-gray-500 hover:text-green-600"
                      >
                        <FaHistory size={14} />
                      </button>
                      {(s.bank_details_status === 'NEEDS_REVIEW' || s.bank_details_status === 'CONFIRMED') && (
                        <button
                          onClick={() => setOverrideModal({ supplier: s, reason: '' })}
                          title={s.bank_details_status === 'NEEDS_REVIEW' ? 'Verify change' : 'Verify for QuickBooks'}
                          className="p-2 rounded-lg hover:bg-amber-50 text-gray-500 hover:text-amber-600"
                        >
                          <FaClipboardCheck size={14} />
                        </button>
                      )}
                      {(s.bank_details_status === 'VERIFIED_FOR_QBO' || s.bank_details_status === 'CONFIRMED') && s.qbo_supplier_id && (
                        <button
                          onClick={() => setPushTarget(s)}
                          title="Push bank details to QuickBooks"
                          className="p-2 rounded-lg hover:bg-green-50 text-gray-500 hover:text-green-600"
                        >
                          <FaLink size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-400">No suppliers found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      {/* Payment terms modal */}
      {termsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setTermsModal(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-1">Payment Terms</h2>
            <p className="text-sm text-gray-500 mb-4">{termsModal.supplier.company_name}</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
            <select
              value={termsModal.terms}
              onChange={(e) => setTermsModal({ ...termsModal, terms: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-4"
            >
              {TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-1">Credit Limit (R)</label>
            <input
              type="number"
              min="0"
              value={termsModal.credit_limit}
              onChange={(e) => setTermsModal({ ...termsModal, credit_limit: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-6"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setTermsModal(null)} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={saveTerms} disabled={savingTerms} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                {savingTerms ? <FaSpinner className="animate-spin" size={14} /> : <FaCheck size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Override modal */}
      {overrideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setOverrideModal(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-1 flex items-center gap-2">
              <FaExclamationTriangle className="text-amber-500" /> Verify Bank Details
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {overrideModal.supplier.company_name} — marking these details verified confirms the account for QuickBooks payments.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (required)</label>
            <textarea
              value={overrideModal.reason}
              onChange={(e) => setOverrideModal({ ...overrideModal, reason: e.target.value })}
              rows="3"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-6"
              placeholder="e.g. Confirmed by phone with the subbie, verbal confirmation matches"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setOverrideModal(null)} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={saveOverride} disabled={savingOverride || !overrideModal.reason.trim()} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                {savingOverride ? <FaSpinner className="animate-spin" size={14} /> : <FaCheck size={14} />} Verify
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Push to QBO confirm modal */}
      {pushTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setPushTarget(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-2 flex items-center gap-2">
              <FaLink className="text-green-600" /> Push to QuickBooks
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Push confirmed bank details for <strong>{pushTarget.company_name}</strong> to their QuickBooks vendor record?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setPushTarget(null)} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={pushToQbo} disabled={pushing} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                {pushing ? <FaSpinner className="animate-spin" size={14} /> : <FaLink size={14} />} Push to QuickBooks
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History modal */}
      {historyFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setHistoryFor(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-1">Bank Details History</h2>
            <p className="text-sm text-gray-500 mb-4">{historyFor.company_name}</p>
            {loadingHistory ? (
              <div className="py-8 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={18} /></div>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="border border-gray-100 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-green-700 uppercase">{h.action}</span>
                      <span className="text-xs text-gray-400">{new Date(h.created_at).toLocaleString()}</span>
                    </div>
                    {h.note && <p className="text-sm text-gray-600 mt-1">{h.note}</p>}
                    {h.old_status !== h.new_status && (
                      <p className="text-xs text-gray-400 mt-1">{h.old_status} → {h.new_status}</p>
                    )}
                  </div>
                ))}
                {history.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No history yet</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default SuppliersPage;
